import { useState, useMemo, useEffect } from "react";
import {
  Search,
  Shield,
  Check,
  X,
  UserX,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  AlertCircle,
  RefreshCw,
  Eye,
  ArrowLeft,
} from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { makeRequest } from "@/utils/makeRequest";
import { UserRole, UserStatus, type UserProfile } from "@/interfaces/profile";
import Loader from "@/components/loader/Loader";
import { KycStatus } from "@/interfaces/kyc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/services/queryClient";
import { useNavigate } from "react-router-dom";
import { ErrorState } from "@/components/ErrorState";

const AdminUsers = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState<"name" | "date" | "status">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showKycModal, setShowKycModal] = useState(false);

  const [status, setStatus] = useState<UserStatus>(UserStatus.ACTIVE);

  const [adminNote, setAdminNote] = useState("");
  const [selectedAction, setSelectedAction] = useState<UserStatus>();
  const [selectedKycAction, setSelectedKycAction] = useState<
    "APPROVE" | "REJECT" | null
  >(null);

  const itemsPerPage = 10;

  const navigate = useNavigate();

  const fetchUsers = async () => {
    const res = await makeRequest.get("/user", {
      params: { page: currentPage, limit: itemsPerPage },
    });

    return res.data;
  };

  const {
    data: response,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["admin-users", currentPage, itemsPerPage],
    queryFn: fetchUsers,
    // keepPreviousData: true,
  });

  const users: UserProfile[] = response?.data || [];
  const meta = response?.meta;
  console.log("ADMIN USERS", users);
  console.log("META", meta);

  const updateUserMutation = useMutation({
    mutationFn: async ({
      userId,
      updates,
    }: {
      userId: string;
      updates: Partial<UserProfile>;
    }) => {
      return await makeRequest.put(`/user/${userId}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filter, sortBy, sortOrder]);

  const filteredUsers = useMemo(() => {
    if (!users?.length) return [];

    let result = users?.filter((user: UserProfile) => {
      const matchesSearch =
        user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.id.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesFilter =
        filter === "all" ||
        (filter === "verified" && user.kycStatus === KycStatus.APPROVED) ||
        (filter === "pending" && user.kycStatus === KycStatus.PENDING) ||
        (filter === "rejected" && user.kycStatus === KycStatus.REJECTED) ||
        (filter === "admin" && user.role !== UserRole.USER) ||
        (filter === "active" && user.status === UserStatus.ACTIVE) ||
        (filter === "suspended" && user.status === UserStatus.SUSPENDED);

      return matchesSearch && matchesFilter;
    });

    result.sort((a: any, b: any) => {
      let comparison = 0;

      switch (sortBy) {
        case "name":
          comparison = a.name?.localeCompare(b.name);
          break;
        case "date":
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case "status":
          comparison = a.kycStatus.localeCompare(b.kycStatus);
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [users, searchQuery, filter, sortBy, sortOrder]);

  const paginatedUsers = filteredUsers;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 400);
  };

  const handleExport = () => {
    const csvContent = [
      [
        "ID",
        "Name",
        "Email",
        "Role",
        "KYC Status",
        "Status",
        "Created At",
      ].join(","),
      ...filteredUsers.map((u: any) =>
        [
          u.id,
          u.name,
          u.email,
          u.role,
          u.kycStatus,
          u.status,
          u.createdAt,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const getRoleBadge = (role: UserRole) => {
    switch (role) {
      case UserRole.SUPER:
        return (
          <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-500 font-medium">
            Super Admin
          </span>
        );
      case UserRole.ADMIN:
        return (
          <span className="px-2 py-1 rounded text-xs bg-primary/20 text-primary font-medium">
            Admin
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded text-xs bg-muted text-muted-foreground">
            User
          </span>
        );
    }
  };

  const getEmailBadge = (verified: boolean) => {
    return verified ? (
      <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/10 text-emerald-500">
        <Check className="w-3 h-3" /> Verified
      </span>
    ) : (
      <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-yellow-500/10 text-yellow-500">
        <AlertCircle className="w-3 h-3" /> Unverified
      </span>
    );
  };

  const getStatusBadge = (status: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVE:
        return "bg-emerald-500/10 text-emerald-500";
      case UserStatus.SUSPENDED:
        return "bg-yellow-500/10 text-yellow-500";
      case UserStatus.BANNED:
        return "bg-red-600/10 text-red-600";
      case UserStatus.DELETED:
        return "bg-gray-500/10 text-gray-500";
    }
  };

  const getKycBadge = (status: string) => {
    switch (status) {
      case "APPROVED":
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/10 text-emerald-500">
            <Check className="w-3 h-3" /> Verified
          </span>
        );
      case "PENDING":
      case "UNDER_REVIEW":
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-primary/10 text-primary">
            <Shield className="w-3 h-3" /> Pending
          </span>
        );
      case "REJECTED":
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/10 text-red-500">
            <X className="w-3 h-3" /> Rejected
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-muted text-muted-foreground">
            <AlertCircle className="w-3 h-3" /> Not Started
          </span>
        );
    }
  };

  const MobileUserCard = ({ user }: { user: UserProfile }) => {
    return (
      <div className="md:hidden border rounded-lg p-2 space-y-3 bg-card">
        {/* USER HEADER */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold">
            {user.firstName?.[0]}
          </div>

          <div className="min-w-0">
            <p className="font-medium truncate">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {user.email}
            </p>
          </div>
        </div>

        {/* ROLE */}
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Role</span>
          {getRoleBadge(user.role)}
        </div>

        {/* KYC */}
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">KYC</span>
          {getKycBadge(user.kycStatus)}
        </div>

        {/* STATUS */}
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Status</span>
          <span
            className={`px-2 py-1 rounded text-xs ${getStatusBadge(user.status)}`}
          >
            {user.status}
          </span>
        </div>

        {/* ACTIONS */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={() => navigate(`/admin/users/${user.id}`)}
            className="p-2"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const stats = [
    // { label: "Total Users", value: meta?.total || 0, icon: Shield },
    {
      label: "Verified",
      value: users?.filter((u) => u.kycStatus === KycStatus.APPROVED).length,
      icon: UserCheck,
    },
    {
      label: "Pending KYC",
      value: users?.filter((u) => u.kycStatus === KycStatus.PENDING).length,
      icon: AlertCircle,
    },
    {
      label: "Admins",
      value: users?.filter((u) => u.role === UserRole.ADMIN).length,
      icon: Shield,
    },
  ];

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
        }}
      />
    );

  if (isLoading) return <Loader type="bounce" />;

  return (
    <div className="min-h-screen bg-background sm:px-4 lg:px-6">
      <div className="max-w-7xl mx-auto">
        <Button
          className="hidden lg:flex"
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              User Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage platform users and their permissions
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 bg-card rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              <RefreshCw
                className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-card text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, i) => (
            <div key={i} className="nex-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {stat.value}
                  </p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or ID..."
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-primary"
              >
                <option value="all">All Users</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending KYC</option>
                <option value="rejected">Rejected KYC</option>
                <option value="admin">Admins</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split("-");
                setSortBy(field as "name" | "date" | "status");
                setSortOrder(order as "asc" | "desc");
              }}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-primary"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="status-asc">Status</option>
            </select>
          </div>
        </div>

        {/* Users Table - Responsive with horizontal scroll */}
        <div className="nex-card overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              Loading users...
            </div>
          ) : (
            <>
              <div className="md:hidden space-y-3 p-3">
                {paginatedUsers?.map((user) => (
                  <MobileUserCard key={user.id} user={user} />
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto -mx-4 sm:-mx-0">
                <table className="w-full min-w-[800px] sm:min-w-0">
                  <thead className="bg-card">
                    <tr>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        User
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Email
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Role
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        KYC Status
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Account Status
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Joined
                      </th>
                      <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4 whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers?.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-border hover:bg-muted/50 dark:hover:bg-[#252A32] transition-colors"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-background bg-gradient-to-br from-primary to-primary/70 font-bold uppercase text-black shadow-sm ring-1 ring-border">
                              {`${user?.firstName?.[0] || ""}${
                                user?.lastName?.[0] || ""
                              }`.toUpperCase() || "U"}
                            </div>
                            <div className="min-w-0">
                              <p className="text-foreground font-medium truncate">
                                {user.firstName}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {user.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap">
                          {getEmailBadge(user.emailVerified)}
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap">
                          {getKycBadge(user.kycStatus)}
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded text-xs ${getStatusBadge(user.status)}`}
                          >
                            {user.status}
                          </span>
                          {/* <span
                            className={`px-2 py-1 rounded text-xs ${
                              user.status === UserStatus.ACTIVE
                                ? "bg-emerald-500/10 text-emerald-500"
                                : "bg-red-500/10 text-red-500"
                            }`}
                          >
                            {user.status === UserStatus.ACTIVE
                              ? "Active"
                              : "Suspended"}
                          </span> */}
                        </td>
                        <td className="py-4 px-4 text-muted-foreground text-sm whitespace-nowrap">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => {
                                navigate(`/admin/users/${user.id}`);
                              }}
                              className="p-2.5 min-w-9 min-h-9 text-muted-foreground hover:text-primary transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setSelectedKycAction(
                                  user.kycStatus === KycStatus.APPROVED
                                    ? "REJECT"
                                    : "APPROVE",
                                );
                                setShowKycModal(true);
                              }}
                              className="p-2.5 min-w-9 min-h-9 text-muted-foreground hover:text-primary transition-colors"
                              title="Toggle KYC"
                            >
                              <Shield className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setSelectedAction(
                                  user.status === UserStatus.ACTIVE
                                    ? UserStatus.SUSPENDED
                                    : UserStatus.ACTIVE,
                                );
                                setShowStatusModal(true);
                              }}
                              className={`p-2 transition-colors ${
                                user.status === UserStatus.ACTIVE
                                  ? "text-muted-foreground hover:text-red-500"
                                  : "text-muted-foreground hover:text-emerald-500"
                              }`}
                              title={
                                user.status === UserStatus.ACTIVE
                                  ? "Suspend"
                                  : "Activate"
                              }
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t border-border gap-4">
                <p className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filteredUsers.length)}{" "}
                  of {filteredUsers.length} users
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from(
                    { length: meta?.totalPages || 1 },
                    (_, i) => i + 1,
                  )
                    .slice(
                      Math.max(0, currentPage - 3),
                      Math.min(meta?.total, currentPage + 2),
                    )
                    .map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          currentPage === page
                            ? "bg-primary text-primary-foreground"
                            : "bg-card text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(meta?.total, p + 1))
                    }
                    disabled={currentPage === meta?.total}
                    className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select User Account Status to Update</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {selectedAction === UserStatus.SUSPENDED
                ? "This will restrict the user from trading, logging in, and performing actions."
                : "This will restore full access to the user account."}
            </p>

            <Select
              value={status}
              onValueChange={(value) => setStatus(value as UserStatus)}
            >
              <SelectTrigger className="w-full !h-12">
                <SelectValue placeholder="User Status" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value={UserStatus.ACTIVE}>Activate</SelectItem>
                <SelectItem value={UserStatus.SUSPENDED}>Suspend</SelectItem>
                <SelectItem value={UserStatus.BANNED}>Ban</SelectItem>
                <SelectItem value={UserStatus.DELETED}>Delete</SelectItem>
              </SelectContent>
            </Select>

            <Textarea
              placeholder="Admin note (required for audit)..."
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowStatusModal(false)}
              >
                Cancel
              </Button>

              <Button
                variant={
                  selectedAction === UserStatus.SUSPENDED
                    ? "destructive"
                    : "default"
                }
                onClick={() => {
                  if (!selectedUser || !adminNote.trim()) {
                    toast.warning("Admin note is required");
                    return;
                  }

                  if (!status) {
                    toast.warning("Please select a status");
                    return;
                  }

                  updateUserMutation.mutate(
                    {
                      userId: selectedUser.id,
                      updates: {
                        status,
                      },
                    },
                    {
                      onSuccess: () => {
                        toast.success("Success", {
                          description: `User ${selectedAction?.toLowerCase()}d`,
                        });
                        setShowStatusModal(false);
                        setAdminNote("");
                      },
                    },
                  );
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showKycModal} onOpenChange={setShowKycModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedKycAction === "APPROVE" ? "Approve KYC" : "Reject KYC"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This action overrides the standard KYC workflow. Ensure compliance
              before proceeding.
            </p>

            <Textarea
              placeholder="Reason (required)..."
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowKycModal(false)}>
                Cancel
              </Button>

              <Button
                className={
                  selectedKycAction === "APPROVE"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-red-600 hover:bg-red-700"
                }
                onClick={() => {
                  if (!selectedUser || !adminNote.trim()) {
                    toast.warning("Reason is required");
                    return;
                  }

                  updateUserMutation.mutate(
                    {
                      userId: selectedUser.id,
                      updates: {
                        kycStatus:
                          selectedKycAction === "APPROVE"
                            ? KycStatus.APPROVED
                            : KycStatus.REJECTED,
                      },
                    },
                    {
                      onSuccess: () => {
                        toast.success("KYC Updated", {
                          description: `User KYC ${selectedKycAction?.toLowerCase()}d`,
                        });
                        setShowKycModal(false);
                        setAdminNote("");
                      },
                    },
                  );
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
